import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PartyPopper, Share2, Plane, Trophy, Users, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface TripCompletedCelebrationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destination: string;
  tripLength: number;
  tripType?: string;
  packedItems: number;
  totalItems: number;
  onShareCard: () => void;
  onInviteFriends: () => void;
  onContinue: () => void;
  onFinalizeTrip?: () => void;
}

export function TripCompletedCelebration({
  open,
  onOpenChange,
  destination,
  tripLength,
  tripType,
  packedItems,
  totalItems,
  onShareCard,
  onInviteFriends,
  onContinue,
  onFinalizeTrip
}: TripCompletedCelebrationProps) {
  const [showContent, setShowContent] = useState(false);

  const getTripMessage = () => {
    const type = tripType?.toLowerCase() || '';
    if (type.includes('business')) return 'Have a safe & productive business trip!';
    if (type.includes('adventure')) return 'Have a safe trip & enjoy your adventure!';
    if (type.includes('beach') || type.includes('relaxation')) return 'Have a safe trip & enjoy your relaxation!';
    if (type.includes('romantic') || type.includes('honeymoon')) return 'Have a wonderful romantic getaway!';
    if (type.includes('family')) return 'Have a safe trip & enjoy your family time!';
    if (type.includes('solo')) return 'Have a safe trip & enjoy your solo adventure!';
    if (type.includes('cultural')) return 'Have a safe trip & enjoy exploring!';
    if (type.includes('city')) return 'Have a safe trip & enjoy the city!';
    return 'Have a safe trip & enjoy your travels!';
  };

  useEffect(() => {
    if (open) {
      // Trigger confetti burst
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);

        // Launch confetti from both sides
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444']
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444']
        });
      }, 250);

      // Show content after initial confetti
      setTimeout(() => setShowContent(true), 500);

      return () => clearInterval(interval);
    } else {
      setShowContent(false);
    }
  }, [open]);

  const handleShare = () => {
    // Call the parent handler directly - it will handle closing this dialog
    onShareCard();
    // Additional confetti on share action
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10b981', '#06b6d4']
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden">
        <AnimatePresence>
          {showContent && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', duration: 0.5 }}
            >
              <DialogHeader className="text-center pb-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="mx-auto mb-4"
                >
                  <div className="relative">
                    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto">
                      <Trophy className="h-10 w-10 text-primary" />
                    </div>
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute -top-2 -right-2"
                    >
                      <PartyPopper className="h-8 w-8 text-amber-500" />
                    </motion.div>
                  </div>
                </motion.div>

                <DialogTitle className="text-2xl">
                  You're All Packed! 🎉
                </DialogTitle>
                <DialogDescription className="text-base">
                  {tripLength}-day trip to {destination}
                </DialogDescription>
                <p className="text-primary font-medium mt-2">
                  {getTripMessage()} ✈️
                </p>
              </DialogHeader>

              <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 mb-6">
                <CardContent className="p-6 text-center">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-2"
                  >
                    <div className="text-4xl font-bold text-primary">
                      {packedItems}/{totalItems}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      items packed and ready to go
                    </p>
                  </motion.div>
                </CardContent>
              </Card>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-3"
              >
                {/* Share Your Success */}
                <Button 
                  onClick={handleShare} 
                  className="w-full gap-2 h-12"
                  size="lg"
                >
                  <Share2 className="h-5 w-5" />
                  Share Your Packing Success
                </Button>

                {/* Invite Travel Buddies */}
                <Button 
                  onClick={onInviteFriends}
                  variant="outline" 
                  className="w-full gap-2 h-12"
                  size="lg"
                >
                  <Users className="h-5 w-5" />
                  Invite Travel Buddies
                </Button>

                {/* Continue */}
                <Button 
                  onClick={onContinue} 
                  variant="ghost" 
                  className="w-full gap-2"
                >
                  <Plane className="h-4 w-4" />
                  Continue to Trip Details
                </Button>

                {/* I am ready to go - Finalize Trip */}
                {onFinalizeTrip && (
                  <Button 
                    onClick={onFinalizeTrip}
                    variant="default" 
                    className="w-full gap-2 h-12 bg-primary hover:bg-primary/90"
                    size="lg"
                  >
                    <CheckCircle2 className="h-5 w-5" />
                    I am ready to go!
                  </Button>
                )}
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-center text-xs text-muted-foreground mt-4"
              >
                Share your packing journey and help friends pack smarter! 
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
